import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import axiosInstance from '../services/axiosConfig'
import { Line } from 'react-chartjs-2'
import {
    Chart as ChartJS, LineElement, CategoryScale, LinearScale, PointElement,
    Title, Tooltip, Legend
} from 'chart.js'
import zoomPlugin from 'chartjs-plugin-zoom'
import moment from 'moment'
import 'moment/locale/tr'
import * as XLSX from 'xlsx'
import Store from '../Store/Store'
import { set_combine_trends, set_external_trend_info, set_external_trend_info_cop, set_tag_list, set_tags_for_trend_page } from '../Action/Action'
import loading_ from "../Assets2/loading.gif"
import "./CSS/Raporlama.css"
import 'chartjs-adapter-moment'

// Set moment to Turkish locale
moment.locale('tr')

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend, zoomPlugin)

const Raporlama = ({ tags, external_trend_info, external_trend_info_cop, tags_for_trend_page,
    combine_trends, totalizer_tags, uid, yetkilendirme, temp_com_id }) => {

    const history = useHistory()
    const location = useLocation()

    // Core States
    const [tagList, setTagList] = useState(() => {
        // İlk render: her iki kaynaktan da benzersiz birleşik liste oluştur
        const listA = Array.isArray(tags) ? tags : []
        const listB = Array.isArray(tags_for_trend_page) ? tags_for_trend_page : []
        const merged = [...listA, ...listB]
        const uniqueById = []
        const seen = new Set()
        for (const t of merged) {
            const id = String(t?.tagId)
            if (!seen.has(id)) {
                seen.add(id)
                uniqueById.push(t)
            }
        }
        return uniqueById
    })
    const [selectedTags, setSelectedTags] = useState([])
    const [searchQuery, setSearchQuery] = useState("")

    // Date States
    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(null)
    const [datePreset, setDatePreset] = useState("custom")

    // Data States
    const [trendData, setTrendData] = useState([])
    const [loading, setLoading] = useState(false)
    const [minValue, setMinValue] = useState("")
    const [maxValue, setMaxValue] = useState("")

    // UI States
    const [showTagModal, setShowTagModal] = useState(false)
    const [showDateDropdown, setShowDateDropdown] = useState(false)
    const [hasPermission, setHasPermission] = useState(false)
    const [urlParamsProcessed, setUrlParamsProcessed] = useState(false)

    // Permission Check - once granted, never revoke during this session
    useEffect(() => {
        // Already permitted, don't re-check (prevents revoking when external_trend_info is cleared)
        if (hasPermission) return

        // Check if coming from SCADA page with direct link (has URL parameters)
        const searchParams = new URLSearchParams(location.search)
        const hasDirectLink = searchParams.has('tagId') && searchParams.has('startDate')

        if (hasDirectLink) {
            setHasPermission(true)
            return
        }

        // Allow access when coming from SCADA widget (via Redux state)
        if (external_trend_info?.length > 0) {
            setHasPermission(true)
            return
        }

        if (yetkilendirme && uid) {
            const userPerms = yetkilendirme[uid]
            if (userPerms?.["Raporlama"]?.["O"] === "t") {
                setHasPermission(true)
            } else {
                setTimeout(() => history.goBack(), 2500)
            }
        }
    }, [yetkilendirme, uid, history, location.search, external_trend_info, hasPermission])

    // Fetch all tags and vars from backend API on mount
    useEffect(() => {
        const fetchTagsAndVars = async () => {
            if (!temp_com_id) return

            try {
                // Fetch tags
                const tagResponse = await axiosInstance.post('/tags/GetTaglist', { companyId: Number(temp_com_id) })
                const tagData = tagResponse.data

                // Fetch vars
                const varResponse = await axiosInstance.get(`/DataSource/variables?companyId=${temp_com_id}`)
                const varPayload = varResponse.data
                const vars = varPayload.Vars || varPayload.vars || []

                // Combine tags and vars
                const allItems = []

                if (tagData && tagData.length > 0) {
                    allItems.push(...tagData)
                }

                // Add vars with special format
                if (vars && vars.length > 0) {
                    vars.forEach(v => {
                        allItems.push({
                            tagId: `var_${v.VarId || v.varId}`,
                            tagName: `[VAR] ${v.VarName || v.varName}`,
                            isVar: true,
                            varId: v.VarId || v.varId
                        })
                    })
                }

                if (allItems.length > 0) {
                    setTagList(allItems)
                }
            } catch (error) {
                // Silent error
            }
        }

        fetchTagsAndVars()
    }, [temp_com_id])

    // Parse URL query parameters for direct linking from SCADA pages
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search)
        const urlTagId = searchParams.get('tagId')
        const urlStartDate = searchParams.get('startDate')
        const urlEndDate = searchParams.get('endDate')
        const urlPeriod = searchParams.get('period')

        // Only process if we have all required parameters and tagList is available
        if (urlTagId && urlStartDate && urlEndDate && tagList.length > 0 && !urlParamsProcessed) {
            console.log('📊 Raporlama: Processing URL parameters:', { urlTagId, urlStartDate, urlEndDate, urlPeriod })

            // Find the tag in tagList
            const tag = tagList.find(t => String(t.tagId) === String(urlTagId))

            if (tag) {
                console.log('📊 Raporlama: Found tag:', tag.tagName)
                setSelectedTags([tag])
                setStartDate(urlStartDate)
                setEndDate(urlEndDate)
                setUrlParamsProcessed(true) // Mark as processed to prevent double fetch

                // Set appropriate preset based on period
                if (urlPeriod === 'daily') {
                    setDatePreset('yesterday')
                } else if (urlPeriod === 'monthly') {
                    setDatePreset('thismonth')
                } else if (urlPeriod === 'yearly') {
                    setDatePreset('custom')
                } else {
                    setDatePreset('custom')
                }
            } else {
                console.warn('⚠️ Raporlama: Tag not found in tagList:', urlTagId)
            }
        }
    }, [location.search, tagList, urlParamsProcessed])

    // Tag List Management
    useEffect(() => {
        // Her iki listeyi de birleştir ve benzersiz hale getir (Var ile sınırlama yok)
        const listA = Array.isArray(tags) ? tags : []
        const listB = Array.isArray(tags_for_trend_page) ? tags_for_trend_page : []
        const merged = [...listA, ...listB]
        const uniqueById = []
        const seen = new Set()
        for (const t of merged) {
            const id = String(t?.tagId)
            if (!seen.has(id)) {
                seen.add(id)
                uniqueById.push(t)
            }
        }
        setTagList(uniqueById)
    }, [tags, tags_for_trend_page])

    // Debug logları temizlendi

    // Fallback: Tag listeleri boşsa API'den çek (GetTaglist - GetTags endpoint'i yok)
    useEffect(() => {
        const hasWindow = typeof window !== 'undefined'
        const hasFetch = typeof fetch === 'function'
        const shouldFetch = (!tags || tags.length === 0) && (!tags_for_trend_page || tags_for_trend_page.length === 0)
        if (!shouldFetch || !temp_com_id || !hasWindow || !hasFetch) return

        let cancelled = false

        const fetchAllTags = async () => {
            try {
                let data = []
                const res = await axiosInstance.post('/tags/GetTaglist', { companyId: String(temp_com_id) })
                data = res.data

                // Benzersiz hale getir
                const unique = []
                const seen = new Set()
                for (const t of Array.isArray(data) ? data : []) {
                    const id = String(t?.tagId)
                    if (!seen.has(id)) { seen.add(id); unique.push(t) }
                }

                if (!cancelled) {
                    setTagList(unique)
                    try { Store.dispatch(set_tag_list(unique)) } catch { }
                }
            } catch (err) {
                // Silent error
            }

            // Ayrıca tags_for_trend_page boş ise doldur
            try {
                const r = await axiosInstance.post('/tags/GetTagsForTrend', { companyId: String(temp_com_id) })
                const trendTags = r.data
                if (!cancelled && Array.isArray(trendTags) && trendTags.length > 0) {
                    try { Store.dispatch(set_tags_for_trend_page(trendTags)) } catch { }
                    // local liste ile birleştir
                    setTagList(prev => {
                        const merged = [...prev, ...trendTags]
                        const uniq = []
                        const seen2 = new Set()
                        for (const t of merged) {
                            const id = String(t?.tagId)
                            if (!seen2.has(id)) { seen2.add(id); uniq.push(t) }
                        }
                        return uniq
                    })
                }
            } catch (err) {
                // Silent error
            }
        }

        fetchAllTags()
        return () => { cancelled = true }
    }, [tags, tags_for_trend_page, temp_com_id])

    // Eğer listede sadece Var tipleri varsa ve trend listesi boşsa, GetTagsForTrend çek
    useEffect(() => {
        const hasNonVar = Array.isArray(tagList) && tagList.some(t => (t?.tip || '').toLowerCase() !== 'var')
        if (hasNonVar || (tags_for_trend_page && tags_for_trend_page.length > 0) || !temp_com_id) return
        let cancelled = false
        const fetchTrendTags = async () => {
            try {
                const r = await axiosInstance.post('/tags/GetTagsForTrend', { companyId: Number(temp_com_id) })
                const trendTags = r.data
                if (!cancelled && Array.isArray(trendTags) && trendTags.length > 0) {
                    try { Store.dispatch(set_tags_for_trend_page(trendTags)) } catch { }
                    setTagList(prev => {
                        const merged = [...prev, ...trendTags]
                        const uniq = []
                        const seen = new Set()
                        for (const t of merged) {
                            const id = String(t?.tagId)
                            if (!seen.has(id)) { seen.add(id); uniq.push(t) }
                        }
                        return uniq
                    })
                }
            } catch (e) {
                // Silent error
            }
        }
        fetchTrendTags()
        return () => { cancelled = true }
    }, [tagList, tags_for_trend_page, temp_com_id])

    // Track the last processed external_trend_info to avoid re-processing
    const lastProcessedTrendRef = React.useRef(null)

    // External Data Handling
    useEffect(() => {
        console.log('📊 External trend effect:', {
            external_trend_info_length: external_trend_info?.length,
            hasPermission,
            tagList_length: tagList.length,
            temp_com_id
        })
        if (!external_trend_info?.length || !hasPermission || !tagList.length) return

        // Create a unique key for this trend info to detect duplicates
        const trendKey = JSON.stringify(external_trend_info[0])
        if (lastProcessedTrendRef.current === trendKey) return
        lastProcessedTrendRef.current = trendKey

        const { tagId, inital_date, last_date, trendType } = external_trend_info[0]

        // Clear external_trend_info immediately to prevent re-triggering
        Store.dispatch(set_external_trend_info([]))

        // Find the tag in tagList and set it as selected
        const tag = tagList.find(t => String(t.tagId) === String(tagId))
        console.log('📊 External trend processing:', { tagId, inital_date, last_date, tagFound: !!tag, tagListSample: tagList.slice(0, 3).map(t => ({ id: t.tagId, name: t.tagName })) })
        if (tag) {
            setSelectedTags([tag])
        }

        // Set the date range
        setStartDate(inital_date)
        setEndDate(last_date)

        // Set appropriate preset based on trend type
        if (trendType === 'daily') {
            setDatePreset('yesterday')
        } else if (trendType === 'monthly') {
            setDatePreset('thismonth')
        } else if (trendType === 'yearly') {
            setDatePreset('custom')
        } else {
            setDatePreset('custom')
        }

        // Fetch the trend data with time-based API selection
        const isTotalizer = totalizer_tags?.some(t => t.tagId === tagId)

        const fetchData = async () => {
            setLoading(true)
            const startDateTime = `${inital_date} 00:00:00.000`
            const endDateTime = `${last_date} 23:59:59.999`

            try {
                const date1 = new Date(inital_date)
                const date2 = new Date(last_date)
                const diffDays = Math.ceil((date2 - date1) / (1000 * 60 * 60 * 24))
                const diffMonths = date2.getMonth() - date1.getMonth() + (12 * (date2.getFullYear() - date1.getFullYear()))
                const diffYears = date2.getFullYear() - date1.getFullYear()

                let endpoint = "/tags/GetTagTrend"

                if (isTotalizer) {
                    if (diffDays === 1) {
                        endpoint = "/tags/GetTagTrendTotalizerDay"
                    } else if (diffYears >= 1) {
                        endpoint = "/tags/GetTagTrendTotalizerYear"
                    } else if (diffMonths >= 1) {
                        endpoint = "/tags/GetTagTrendTotalizerDay"
                    } else {
                        endpoint = "/tags/GetTagTrendTotalizerDay"
                    }
                }

                console.log('📊 Fetching trend:', { endpoint, tagId, startDateTime, endDateTime, companyId: temp_com_id })
                const response = await axiosInstance.post(endpoint, { TagId: tagId, StartDate: startDateTime, EndDate: endDateTime, companyId: String(temp_com_id) })
                const data = response.data
                console.log('📊 Trend data received:', data?.length, 'records')

                const processedData = data.map(item => ({
                    date: item.valueDate,
                    value: parseFloat(item.tagValue.replace(/,/g, '')),
                    tagId: tagId
                }))

                processedData.sort((a, b) => new Date(a.date) - new Date(b.date))
                setTrendData(processedData)
            } catch (error) {
                console.error('📊 Trend fetch error:', error)
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [external_trend_info, hasPermission, totalizer_tags, tagList, temp_com_id])

    // Combine Trends Handling (VRF Widget Multi-Tag Selection)
    useEffect(() => {
        if (combine_trends?.length > 0 && hasPermission && tagList.length > 0) {
            // VRF'den gelen tag ID'lerini al
            const vrfTagIds = combine_trends.map(t => String(t.tagId))

            // Dış hava sıcaklık ve nem tag'larını bul ve ekle (eski program mantığı)
            tagList.forEach(tag => {
                const tagNameLower = tag.tagName?.toLowerCase() || ""

                // Dış hava sıcaklık: İngilizce ve Türkçe karakterleri destekle
                const isSicaklik =
                    // İngilizce versiyonlar (alt çizgili)
                    tagNameLower.includes("dishava_sic") ||
                    tagNameLower.includes("dis_hava_sic") ||
                    tagNameLower.includes("dishava_sicaklik") ||
                    tagNameLower.includes("dis_hava_sicaklik") ||
                    // Türkçe versiyonlar (boşluklu ve Türkçe karakterli)
                    tagNameLower.includes("dış hava sıcak") ||
                    tagNameLower.includes("dışhava sıcak") ||
                    tagNameLower.includes("dış_hava_sıcak") ||
                    tagNameLower.includes("dışsıcaklık") ||
                    // Tam eşleşme
                    (tagNameLower.includes("dış") && tagNameLower.includes("hava") && tagNameLower.includes("sıcak"))

                const isNem =
                    tagNameLower.includes("dishava_nem") ||
                    tagNameLower.includes("dis_hava_nem") ||
                    tagNameLower.includes("dış hava nem") ||
                    tagNameLower.includes("dışhava nem") ||
                    tagNameLower.includes("dış_hava_nem")

                if (isSicaklik || isNem) {
                    if (!vrfTagIds.includes(String(tag.tagId))) {
                        vrfTagIds.push(String(tag.tagId))
                    }
                }
            })

            // VRF tag'larına karşılık gelen tam tag objelerini bul
            const tagsToSelect = tagList.filter(tag =>
                vrfTagIds.includes(String(tag.tagId))
            )

            // Tüm tag'ları otomatik seç
            setSelectedTags(tagsToSelect)

            // 10 saniye sonra combine_trends'i temizle
            setTimeout(() => Store.dispatch(set_combine_trends([])), 10000)
        }
    }, [combine_trends, hasPermission, tagList])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            Store.dispatch(set_external_trend_info([]))
            Store.dispatch(set_external_trend_info_cop([]))
            Store.dispatch(set_combine_trends([]))
        }
    }, [])

    // Auto-calculate MIN/MAX from trend data
    useEffect(() => {
        if (trendData.length > 0) {
            const values = trendData.map(d => d.value).filter(v => !isNaN(v))
            if (values.length > 0) {
                let min = Math.min(...values)
                let max = Math.max(...values)

                // If all values are the same (constant value), add margin
                if (min === max) {
                    const margin = Math.abs(min) * 0.1 || 1 // 10% margin or 1 if value is 0
                    min = min - margin
                    max = max + margin
                }

                setMinValue(min.toFixed(2))
                setMaxValue(max.toFixed(2))
            }
        }
    }, [trendData])

    // Date Preset Functions
    const getDatePreset = useCallback((preset) => {
        const today = new Date()
        const formatDate = (date) => {
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
        }

        const presets = {
            today: { start: formatDate(today), end: formatDate(today), label: 'Bugün' },
            yesterday: (() => {
                const yesterday = new Date(today)
                yesterday.setDate(yesterday.getDate() - 1)
                return { start: formatDate(yesterday), end: formatDate(yesterday), label: 'Dün' }
            })(),
            last5days: (() => {
                const last5 = new Date(today)
                last5.setDate(last5.getDate() - 5)
                return { start: formatDate(last5), end: formatDate(today), label: 'Son 5 Gün' }
            })(),
            last30days: (() => {
                const last30 = new Date(today)
                last30.setDate(last30.getDate() - 30)
                return { start: formatDate(last30), end: formatDate(today), label: 'Son 30 Gün' }
            })(),
            thisweek: (() => {
                const dayOfWeek = today.getDay()
                const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1
                const weekStart = new Date(today)
                weekStart.setDate(today.getDate() - diff)
                return { start: formatDate(weekStart), end: formatDate(today), label: 'Bu Hafta' }
            })(),
            thismonth: (() => {
                const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)
                return { start: formatDate(monthStart), end: formatDate(today), label: 'Bu Ay' }
            })(),
            lastmonth: (() => {
                const monthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1)
                const monthEnd = new Date(today.getFullYear(), today.getMonth(), 0)
                return { start: formatDate(monthStart), end: formatDate(monthEnd), label: 'Geçen Ay' }
            })()
        }

        return presets[preset] || { start: startDate || '', end: endDate || '', label: 'Özel Aralık' }
    }, [startDate, endDate])

    const applyDatePreset = useCallback((preset) => {
        const { start, end } = getDatePreset(preset)
        setStartDate(start)
        setEndDate(end)
        setDatePreset(preset)
        setShowDateDropdown(false)
    }, [getDatePreset])

    // Fetch Trend Data with time-based API selection for totalizers (supports both Tags and Vars)
    const fetchTrendData = useCallback(async (tagIds, start, end, isTotalizer = "0") => {
        if (!hasPermission || !start || !end || tagIds.length === 0) return

        setLoading(true)

        try {
            const allData = []

            // Calculate time differences (like old program)
            const date1 = new Date(start)
            const date2 = new Date(end)
            const diffDays = Math.ceil((date2 - date1) / (1000 * 60 * 60 * 24))
            const diffMonths = date2.getMonth() - date1.getMonth() + (12 * (date2.getFullYear() - date1.getFullYear()))
            const diffYears = date2.getFullYear() - date1.getFullYear()

            for (const tagId of tagIds) {
                let startDateTime = `${start} 00:00:00.000`
                let endDateTime = `${end} 23:59:59.999`

                // Check if this is a Var (starts with "var_")
                const isVar = String(tagId).startsWith('var_')
                const actualId = isVar ? String(tagId).replace('var_', '') : tagId

                let endpoint, bodyData

                if (isVar) {
                    // Use GetVarTrend for variables
                    endpoint = "/tags/GetVarTrend"
                    bodyData = { VarId: parseInt(actualId), StartDate: startDateTime, EndDate: endDateTime }
                } else {
                    // Use GetTagTrend or Totalizer endpoints for tags
                    endpoint = "/tags/GetTagTrend"

                    // Totalizer API selection based on time range (eski program mantığı)
                    if (isTotalizer === "1") {
                        if (diffDays === 1) {
                            endpoint = "/tags/GetTagTrendTotalizerDay"
                        } else if (diffYears >= 1) {
                            endpoint = "/tags/GetTagTrendTotalizerYear"
                        } else if (diffMonths >= 1) {
                            endpoint = "/tags/GetTagTrendTotalizerDay"
                        } else {
                            endpoint = "/tags/GetTagTrendTotalizerDay"
                        }
                    }
                    bodyData = { TagId: actualId, StartDate: startDateTime, EndDate: endDateTime, companyId: String(temp_com_id) }
                }

                const response = await axiosInstance.post(endpoint, bodyData)
                const data = response.data

                data.forEach(item => {
                    // Handle both tag and var response formats
                    const valueStr = item.tagValue || item.varValue || item.VarValue || '0'
                    const value = parseFloat(String(valueStr).replace(/,/g, ''))
                    const dateStr = item.valueDate || item.adDate || item.AdDate
                    allData.push({
                        date: dateStr,
                        value: value,
                        tagId: tagId
                    })
                })
            }

            allData.sort((a, b) => new Date(a.date) - new Date(b.date))
            setTrendData(allData)

        } catch (error) {
            // Silent error
        } finally {
            setLoading(false)
        }
    }, [hasPermission, temp_com_id])

    // Tag Selection Handler - Always multi-select mode
    const handleTagSelect = useCallback((tag) => {
        setSelectedTags(prev => {
            const tagId = String(tag.tagId)
            if (prev.some(t => String(t.tagId) === tagId)) {
                return prev.filter(t => String(t.tagId) !== tagId)
            }
            return [...prev, tag]
        })
    }, [])

    // Show Trend Handler - Auto-detect single vs multi
    const handleShowTrend = useCallback(() => {
        if (!startDate || !endDate || selectedTags.length === 0) return

        const tagIds = selectedTags.map(t => t.tagId)

        // Single tag: check if it's a totalizer
        if (selectedTags.length === 1) {
            const isTotalizer = totalizer_tags?.some(t => t.tagId === selectedTags[0].tagId)
            fetchTrendData(tagIds, startDate, endDate, isTotalizer ? "1" : "0")
        } else {
            // Multiple tags: regular trend
            fetchTrendData(tagIds, startDate, endDate, "0")
        }
    }, [startDate, endDate, selectedTags, fetchTrendData, totalizer_tags])

    // Auto-fetch trend when tag and dates are selected
    useEffect(() => {
        if (selectedTags.length > 0 && startDate && endDate) {
            handleShowTrend()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTags, startDate, endDate])

    // Export to Excel
    const exportToExcel = useCallback(() => {
        const excelData = [['Tarih', 'Değer', 'Tag']]
        trendData.forEach(item => {
            const tag = tagList.find(t => String(t.tagId) === String(item.tagId))
            excelData.push([
                moment(item.date).format('DD/MM/YYYY HH:mm:ss'),
                item.value,
                tag?.tagName || item.tagId
            ])
        })

        const wb = XLSX.utils.book_new()
        const ws = XLSX.utils.aoa_to_sheet(excelData)
        XLSX.utils.book_append_sheet(wb, ws, 'Trend Data')
        XLSX.writeFile(wb, `Trend-${startDate}-${endDate}.xlsx`)
    }, [trendData, tagList, startDate, endDate])

    // Clear Data
    const clearData = useCallback(() => {
        setTrendData([])
        setSelectedTags([])
        setStartDate(null)
        setEndDate(null)
        setDatePreset("custom")
        setMinValue("")
        setMaxValue("")
    }, [])

    // Filtered Tag List
    const filteredTags = useMemo(() => {
        if (!searchQuery) return tagList
        return tagList.filter(tag =>
            tag.tagName?.toLowerCase().includes(searchQuery.toLowerCase())
        )
    }, [tagList, searchQuery])

    // Chart Data
    const chartData = useMemo(() => {
        const uniqueTagIds = [...new Set(trendData.map(item => item.tagId))]
        const colors = ['#000', '#ee82ee', '#80ff00', '#9400d3', '#ffa500', '#a52a2a', '#ff69b4', '#ffd700']

        const datasets = uniqueTagIds.map((tagId, index) => {
            const tagData = trendData.filter(item => item.tagId === tagId)
            const tag = tagList.find(t => String(t.tagId) === String(tagId))

            return {
                label: tag?.tagName || `Tag ${tagId}`,
                data: tagData.map(d => ({
                    x: d.date,
                    y: d.value
                })),
                borderColor: colors[index % colors.length],
                backgroundColor: 'transparent',
                pointRadius: 2,
                borderWidth: 2,
                tension: 0.1
            }
        })

        return { datasets }
    }, [trendData, tagList])

    // Chart Options
    const chartOptions = useMemo(() => {
        // Calculate Y axis range with margin for constant values
        let yMin = minValue ? parseFloat(minValue) : undefined;
        let yMax = maxValue ? parseFloat(maxValue) : undefined;

        // If no manual min/max and we have trend data, calculate from data
        if (!yMin && !yMax && trendData.length > 0) {
            const values = trendData.map(d => parseFloat(d.value)).filter(v => !isNaN(v));
            if (values.length > 0) {
                const dataMin = Math.min(...values);
                const dataMax = Math.max(...values);

                // If all values are the same (constant value), add margin
                if (dataMin === dataMax) {
                    const margin = Math.abs(dataMin) * 0.1 || 1; // 10% margin or 1 if value is 0
                    yMin = dataMin - margin;
                    yMax = dataMax + margin;
                } else {
                    yMin = dataMin;
                    yMax = dataMax;
                }
            }
        }

        return {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                x: {
                    type: 'time',
                    time: {
                        unit: 'hour',
                        displayFormats: {
                            hour: 'D MMM, HH:mm',
                            day: 'D MMM YYYY'
                        }
                    },
                    ticks: { source: 'auto' }
                },
                y: {
                    min: yMin,
                    max: yMax,
                    beginAtZero: false
                }
            },
        plugins: {
            legend: { display: true },
            tooltip: {
                callbacks: {
                    title: (context) => moment(context[0].label).format('D MMMM YYYY, HH:mm:ss')
                }
            },
            zoom: {
                pan: { enabled: true, mode: 'x' },
                zoom: { drag: { enabled: true, backgroundColor: 'rgba(255,255,0,0.3)' }, mode: 'x' }
            }
        }
        }
    }, [minValue, maxValue, trendData])

    // Loading Screen
    if (!hasPermission) {
        return (
            <div className="export_loading export_loading_active">
                <img src={loading_} alt="loading" style={{ width: "40%", position: "relative", left: "30%" }} />
                <p style={{ textAlign: "center" }}>Yetki kontrol ediliyor...</p>
            </div>
        )
    }

    return (
        <>
            <div className="export_loading">
                <img src={loading_} alt="loading" style={{ width: "40%", position: "relative", left: "30%" }} />
                <p style={{ textAlign: "center" }}>İşlem yapılıyor...</p>
            </div>

            <section className="rapor">
                {/* Modern Header - Reorganized with separate panels */}
                <div className="rapor_header_modern compact">
                    {/* Panel 1: Tag Selection */}
                    <div className="control_panel tag_selection_panel">
                        <div className="panel_header">
                            <i className="fa fa-tags"></i>
                            <span>Tag Seçimi</span>
                        </div>
                        <div className="panel_content">
                            <button
                                className="tag_selector_button"
                                onClick={() => setShowTagModal(true)}
                            >
                                <i className="fa fa-tags"></i>
                                Tag Seç
                            </button>

                            {selectedTags.length > 0 && (
                                <div className="selected_tags_container">
                                    <div className="selected_tags_header">
                                        <span><i className="fa fa-list"></i> Seçili Taglar</span>
                                        <button
                                            className="clear_all_tags_btn"
                                            onClick={() => setSelectedTags([])}
                                        >
                                            <i className="fa fa-trash"></i> Tümünü Temizle
                                        </button>
                                    </div>
                                    <div className="selected_tags_chips">
                                        {selectedTags.map((tag, idx) => (
                                            <div key={idx} className="selected_tag_chip">
                                                <div className="chip_content">
                                                    <span className="chip_name">{tag.tagName}</span>
                                                    <div className="chip_details">
                                                        <span><i className="fa fa-hashtag"></i>{tag.tagId}</span>
                                                        {tag.tagName2 && <span><i className="fa fa-ruler"></i>{tag.tagName2}</span>}
                                                    </div>
                                                </div>
                                                <button
                                                    className="chip_remove"
                                                    onClick={() => setSelectedTags(prev => prev.filter((_, i) => i !== idx))}
                                                >
                                                    <i className="fa fa-times"></i>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {selectedTags.length === 0 && (
                                <div className="tag_placeholder">
                                    <i className="fa fa-info-circle"></i>
                                    <span>Trend görüntülemek için tag seçin</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Panel 2: Date Selection */}
                    <div className="control_panel date_selection_panel">
                        <div className="panel_header">
                            <i className="fa fa-calendar"></i>
                            <span>Tarih Aralığı</span>
                        </div>
                        <div className="panel_content">
                            <div className="date_range_selector">
                                <div className="date_range_display_button" onClick={() => setShowDateDropdown(!showDateDropdown)}>
                                    <div className="date_range_text">
                                        <i className="fa fa-calendar"></i>
                                        <span>{startDate && endDate ? (datePreset !== 'custom' ? getDatePreset(datePreset).label : `${startDate} - ${endDate}`) : 'Tarih Seçin'}</span>
                                    </div>
                                    <i className={`fa fa-chevron-${showDateDropdown ? 'up' : 'down'}`}></i>
                                </div>

                                {showDateDropdown && (
                                    <div className="date_range_dropdown active">
                                        <div className="preset_options_list">
                                            {['today', 'yesterday', 'last5days', 'last30days', 'thisweek', 'thismonth', 'lastmonth'].map(preset => (
                                                <div
                                                    key={preset}
                                                    className={`preset_option_item ${datePreset === preset ? 'selected' : ''}`}
                                                    onClick={() => applyDatePreset(preset)}
                                                >
                                                    <i className="fa fa-calendar"></i>
                                                    {getDatePreset(preset).label}
                                                </div>
                                            ))}
                                        </div>
                                        <div className="custom_date_range_inputs">
                                            <label>Başlangıç Tarihi</label>
                                            <input type="date" value={startDate || ''} onChange={(e) => { setStartDate(e.target.value); setDatePreset('custom') }} />
                                            <label>Bitiş Tarihi</label>
                                            <input type="date" value={endDate || ''} onChange={(e) => { setEndDate(e.target.value); setDatePreset('custom') }} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Second row: MIN/MAX and actions */}
                    <div className="compact_row">
                        {/* Panel 3: MIN/MAX Controls - Only show for single tag */}
                        {selectedTags.length === 1 && (
                            <div className="control_panel minmax_panel">
                                <div className="panel_header">
                                    <i className="fa fa-sliders"></i>
                                    <span>Değer Aralığı</span>
                                </div>
                                <div className="panel_content">
                                    <div className="min_max_value_controls">
                                        <div className="value_input_wrapper">
                                            <label>MIN</label>
                                            <input type="number" value={minValue} onChange={(e) => setMinValue(e.target.value)} />
                                            {(selectedTags[0]?.tagUnit || selectedTags[0]?.unit || selectedTags[0]?.tagName2 || selectedTags[0]?.birim) && (
                                                <span className="unit_label">{selectedTags[0].tagUnit || selectedTags[0].unit || selectedTags[0].tagName2 || selectedTags[0].birim}</span>
                                            )}
                                        </div>
                                        <div className="value_input_wrapper">
                                            <label>MAX</label>
                                            <input type="number" value={maxValue} onChange={(e) => setMaxValue(e.target.value)} />
                                            {(selectedTags[0]?.tagUnit || selectedTags[0]?.unit || selectedTags[0]?.tagName2 || selectedTags[0]?.birim) && (
                                                <span className="unit_label">{selectedTags[0].tagUnit || selectedTags[0].unit || selectedTags[0].tagName2 || selectedTags[0].birim}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Panel 4: Action Buttons */}
                        <div className="control_panel actions_panel">
                            <div className="panel_header">
                                <i className="fa fa-cog"></i>
                                <span>İşlemler</span>
                            </div>
                            <div className="panel_content">
                                <div className="action_buttons_group">
                                    {trendData.length > 0 && (
                                        <button className="modern_action_button btn_success" onClick={exportToExcel}>
                                            <i className="fa fa-file-excel"></i>
                                            Export
                                        </button>
                                    )}
                                    <button className="modern_action_button btn_danger" onClick={clearData}>
                                        <i className="fa fa-times"></i>
                                        Temizle
                                    </button>
                                    <button className="modern_action_button btn_primary" onClick={handleShowTrend}>
                                        <i className="fa fa-chart-line"></i>
                                        Göster
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tag Selection Modal */}
                {showTagModal && (
                    <div className="tag_modal_overlay" onClick={() => setShowTagModal(false)}>
                        <div className="tag_selection_modal" onClick={(e) => e.stopPropagation()}>
                            <div className="tag_modal_header">
                                <h3><i className="fa fa-tags"></i> Tag Seçimi ({tagList.length} tag)</h3>
                                <button className="tag_modal_close_btn" onClick={() => setShowTagModal(false)}>
                                    <i className="fa fa-times"></i>
                                </button>
                            </div>

                            <input
                                className="tag_search_input_field"
                                type="text"
                                placeholder="Tag ara..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />

                            <div className="tag_modal_body_content">
                                {filteredTags.length === 0 ? (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '40px 20px',
                                        color: '#999'
                                    }}>
                                        <i className="fa fa-exclamation-circle" style={{ fontSize: '48px', marginBottom: '16px' }}></i>
                                        <p style={{ fontSize: '16px', fontWeight: 600 }}>
                                            {searchQuery ? 'Arama sonucu bulunamadı' : 'Tag listesi yükleniyor...'}
                                        </p>
                                        {!searchQuery && (
                                            <p style={{ fontSize: '14px', marginTop: '8px' }}>
                                                Tag sayısı: {tagList.length}
                                            </p>
                                        )}
                                    </div>
                                ) : (
                                    filteredTags.map((tag, index) => {
                                        const isSelected = selectedTags.some(t => String(t.tagId) === String(tag.tagId))

                                        return (
                                            <div
                                                key={index}
                                                className={`tag_list_single_item ${isSelected ? 'selected_tag_item' : ''}`}
                                                onClick={() => handleTagSelect(tag)}
                                            >
                                                <div className="tag_item_info_block">
                                                    <div className="tag_item_name_text">{tag.tagName}</div>
                                                    <div className="tag_item_metadata">
                                                        <span><i className="fa fa-hashtag"></i> {tag.tagId}</span>
                                                        {tag.tagName2 && <span><i className="fa fa-ruler"></i> {tag.tagName2}</span>}
                                                    </div>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => {}}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                        )
                                    })
                                )}
                            </div>

                            <div className="tag_modal_footer">
                                <button className="modern_action_button btn_primary" onClick={() => setShowTagModal(false)}>
                                    <i className="fa fa-check"></i>
                                    Seçimi Tamamla
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Chart Area */}
                {loading && (
                    <div style={{ marginTop: "20%", textAlign: "center" }}>
                        <img src={loading_} alt="loading" style={{ width: "20%" }} />
                        <h3>Veriler yükleniyor...</h3>
                    </div>
                )}

                {!loading && trendData.length === 0 && (
                    <div style={{ marginTop: "10%", textAlign: "center" }}>
                        <span className='alert_empty_current'>Tag ve tarih seçerek trend görüntüleyin</span>
                    </div>
                )}

                {!loading && trendData.length > 0 && (
                    <div style={{
                        marginTop: "20px",
                        padding: "20px",
                        backgroundColor: "#fff",
                        borderRadius: "12px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        width: "95%",
                        margin: "20px auto",
                        height: "600px"
                    }}>
                        <Line data={chartData} options={chartOptions} />
                    </div>
                )}
            </section>
        </>
    )
}

const mapStateToProps = (state) => ({
    tags: state.project_reducer.tag_list,
    external_trend_info: state.project_reducer.external_trend_info,
    external_trend_info_cop: state.project_reducer.external_trend_info_cop,
    tags_for_trend_page: state.project_reducer.tags_for_trend_page,
    combine_trends: state.project_reducer.combine_trends,
    totalizer_tags: state.project_reducer.totalizer_tags,
    uid: state.auth_reducer.user_id,
    yetkilendirme: state.project_reducer.yetkilendirme_state,
    temp_com_id: state.project_reducer.temp_com_id
})

export default connect(mapStateToProps)(Raporlama)
